import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Badge, Alert, OverlayTrigger, Tooltip, Spinner, ProgressBar, Tabs, Tab } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faServer, faPlus, faTrash, faCheckCircle, faTimesCircle, faInfoCircle, faSync, faPlay, faStop, faFileAlt, faCog } from '@fortawesome/free-solid-svg-icons';
import c2Service from '../../services/c2Service';

/**
 * Redirector Manager Component
 * Deploy and manage C2 redirectors
 */
const RedirectorManager = ({ projectId }) => {
  const [redirectors, setRedirectors] = useState([]);
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Deploy wizard state
  const [showDeployWizard, setShowDeployWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    serverId: '',
    type: 'nginx',
    hostname: '',
    ipAddress: '',
    forwardTo: { type: 'c2', targetUrl: '' },
    filtering: {
      geoFence: { enabled: false, allowedCountries: [] },
      timeWindow: { enabled: false, startHour: 9, endHour: 17 },
      ipWhitelist: [],
      ipBlacklist: []
    },
    letsEncrypt: { enabled: true, email: '' }
  });

  // Deployment progress
  const [deploying, setDeploying] = useState(false);
  const [deploymentProgress, setDeploymentProgress] = useState(null);

  // Log viewer
  const [showLogs, setShowLogs] = useState(false);
  const [logs, setLogs] = useState('');
  const [selectedRedirector, setSelectedRedirector] = useState(null);

  useEffect(() => {
    loadRedirectors();
    loadServers();
  }, [projectId]);

  const loadRedirectors = async () => {
    try {
      setLoading(true);
      const response = await c2Service.getRedirectors({ projectId });
      setRedirectors(response.data.data.redirectors || []);
    } catch (error) {
      console.error('Failed to load redirectors:', error);
      setError('Failed to load redirectors');
    } finally {
      setLoading(false);
    }
  };

  const loadServers = async () => {
    try {
      // Load from localStorage (created by ServerRegistry)
      const storedServers = localStorage.getItem('c2_servers');
      if (storedServers) {
        setServers(JSON.parse(storedServers));
      }
    } catch (error) {
      console.error('Failed to load servers:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleServerSelect = (serverId) => {
    const server = servers.find(s => s.id === serverId);
    if (server) {
      setFormData(prev => ({
        ...prev,
        serverId,
        hostname: server.hostname,
        ipAddress: server.ipAddress,
        letsEncrypt: {
          ...prev.letsEncrypt,
          email: prev.letsEncrypt.email || server.notes?.includes('@') ? server.notes.match(/[\w.-]+@[\w.-]+/)?.[0] : ''
        }
      }));
    }
  };

  const handleDeploy = async () => {
    try {
      setDeploying(true);
      setError(null);

      const server = servers.find(s => s.id === formData.serverId);
      if (!server) {
        throw new Error('Server not found');
      }

      const deployData = {
        name: formData.name,
        hostname: formData.hostname,
        ipAddress: formData.ipAddress,
        type: formData.type,
        projectId,
        forwardTo: formData.forwardTo,
        filtering: formData.filtering,
        ssh: {
          host: server.ipAddress,
          port: server.sshPort,
          username: server.sshUsername,
          password: server.authMethod === 'password' ? server.sshPassword : undefined,
          privateKey: server.authMethod === 'privateKey' ? server.sshPrivateKey : undefined
        },
        letsEncrypt: formData.letsEncrypt
      };

      console.log('[RedirectorManager] Deploying redirector:', deployData.name);

      const response = await c2Service.createRedirector(deployData);
      const redirector = response.data.data;

      setSuccess(`Deployment started for "${formData.name}"`);

      // Poll for deployment progress
      pollDeploymentProgress(redirector._id);

    } catch (error) {
      console.error('Deployment failed:', error);
      setError(`Deployment failed: ${error.response?.data?.error || error.message}`);
      setDeploying(false);
    }
  };

  const pollDeploymentProgress = async (redirectorId) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await c2Service.getRedirector(redirectorId);
        const redirector = response.data.data;

        setDeploymentProgress({
          status: redirector.deployment.status,
          progress: redirector.deployment.progress,
          currentStep: redirector.deployment.currentStep,
          error: redirector.deployment.error
        });

        // Stop polling if deployed or failed
        if (redirector.deployment.status === 'deployed') {
          clearInterval(pollInterval);
          setDeploying(false);
          setSuccess(`Redirector "${redirector.name}" deployed successfully!`);
          setShowDeployWizard(false);
          resetWizard();
          loadRedirectors();
        } else if (redirector.deployment.status === 'failed') {
          clearInterval(pollInterval);
          setDeploying(false);
          setError(`Deployment failed: ${redirector.deployment.error}`);
        }

      } catch (error) {
        console.error('Failed to poll deployment:', error);
        clearInterval(pollInterval);
        setDeploying(false);
      }
    }, 2000); // Poll every 2 seconds

    // Timeout after 10 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
      setDeploying(false);
      setError('Deployment timeout - check redirector status manually');
    }, 600000);
  };

  const handleTest = async (id) => {
    try {
      const response = await c2Service.testRedirector(id);
      if (response.data.success) {
        setSuccess(`Redirector online (${response.data.data.latency}ms)`);
        loadRedirectors();
      } else {
        setError(`Redirector offline: ${response.data.data.message}`);
      }
    } catch (error) {
      setError(`Test failed: ${error.message}`);
    }
  };

  const handleRestart = async (id) => {
    try {
      await c2Service.restartRedirector(id);
      setSuccess('Redirector restarted successfully');
    } catch (error) {
      setError(`Restart failed: ${error.message}`);
    }
  };

  const handleViewLogs = async (redirector) => {
    try {
      setSelectedRedirector(redirector);
      const response = await c2Service.getRedirectorLogs(redirector._id, 100);
      setLogs(response.data.data.logs);
      setShowLogs(true);
    } catch (error) {
      setError(`Failed to load logs: ${error.message}`);
    }
  };

  const handleDelete = async (id, destroyRemote = false) => {
    if (window.confirm(`Are you sure you want to delete this redirector?${destroyRemote ? ' This will also destroy the remote installation.' : ''}`)) {
      try {
        await c2Service.deleteRedirector(id, destroyRemote);
        setSuccess('Redirector deleted successfully');
        loadRedirectors();
      } catch (error) {
        setError(`❌ Delete failed: ${error.message}`);
      }
    }
  };

  const resetWizard = () => {
    setWizardStep(1);
    setFormData({
      name: '',
      serverId: '',
      type: 'nginx',
      hostname: '',
      ipAddress: '',
      forwardTo: { type: 'c2', targetUrl: '' },
      filtering: {
        geoFence: { enabled: false, allowedCountries: [] },
        timeWindow: { enabled: false, startHour: 9, endHour: 17 },
        ipWhitelist: [],
        ipBlacklist: []
      },
      letsEncrypt: { enabled: true, email: '' }
    });
    setDeploymentProgress(null);
  };

  const nextStep = () => setWizardStep(prev => Math.min(prev + 1, 5));
  const prevStep = () => setWizardStep(prev => Math.max(prev - 1, 1));

  const renderTooltip = (text) => <Tooltip>{text}</Tooltip>;

  const getStatusBadge = (redirector) => {
    const status = redirector.deployment.status;
    const isOnline = redirector.health.isOnline;

    if (status === 'deploying') return <Badge bg="info"><Spinner animation="border" size="sm" className="me-1" />Deploying</Badge>;
    if (status === 'failed') return <Badge bg="danger"><FontAwesomeIcon icon={faTimesCircle} className="me-1" />Failed</Badge>;
    if (status === 'deployed' && isOnline) return <Badge bg="success"><FontAwesomeIcon icon={faCheckCircle} className="me-1" />Online</Badge>;
    if (status === 'deployed' && !isOnline) return <Badge bg="warning"><FontAwesomeIcon icon={faTimesCircle} className="me-1" />Offline</Badge>;
    return <Badge bg="secondary">Unknown</Badge>;
  };

  // Wizard Steps
  const renderStep1 = () => (
    <Form.Group>
      <Form.Label>
        Select Server <span className="text-danger">*</span>
        <OverlayTrigger placement="right" overlay={renderTooltip("Choose a registered VPS server for deployment")}>
          <FontAwesomeIcon icon={faInfoCircle} className="ms-2 text-muted" style={{ fontSize: '0.8rem', cursor: 'help' }} />
        </OverlayTrigger>
      </Form.Label>
      <Form.Select
        value={formData.serverId}
        onChange={(e) => handleServerSelect(e.target.value)}
        style={{ backgroundColor: '#374151', color: '#e5e7eb', borderColor: '#4b5563' }}
        required
      >
        <option value="">Choose server...</option>
        {servers.map(server => (
          <option key={server.id} value={server.id}>
            {server.name} ({server.ipAddress}) - {server.serverInfo?.os} {server.serverInfo?.version}
          </option>
        ))}
      </Form.Select>
      {servers.length === 0 && (
        <Alert variant="warning" className="mt-3">
          No servers registered. Please add a server in the Server Registry first.
        </Alert>
      )}
      <Form.Group className="mt-3">
        <Form.Label>Redirector Name <span className="text-danger">*</span></Form.Label>
        <Form.Control
          type="text"
          name="name"
          placeholder="e.g., redirector-us-east"
          value={formData.name}
          onChange={handleInputChange}
          style={{ backgroundColor: '#374151', color: '#e5e7eb', borderColor: '#4b5563' }}
          required
        />
      </Form.Group>
    </Form.Group>
  );

  const renderStep2 = () => (
    <>
      <Form.Group className="mb-3">
        <Form.Label>
          Redirector Type <span className="text-danger">*</span>
          <OverlayTrigger placement="right" overlay={renderTooltip("NGINX (recommended for most use cases), Apache, HAProxy, or Node.js smart proxy")}>
            <FontAwesomeIcon icon={faInfoCircle} className="ms-2 text-muted" style={{ fontSize: '0.8rem', cursor: 'help' }} />
          </OverlayTrigger>
        </Form.Label>
        {['nginx', 'apache', 'haproxy', 'nodejs'].map(type => (
          <Form.Check
            key={type}
            type="radio"
            name="type"
            value={type}
            label={
              <span>
                <strong>{type.toUpperCase()}</strong>
                {type === 'nginx' && ' (Recommended) - Fast, lightweight, easy SSL'}
                {type === 'apache' && ' - Traditional, well-supported'}
                {type === 'haproxy' && ' - High performance load balancer'}
                {type === 'nodejs' && ' - Smart proxy with GeoIP filtering'}
              </span>
            }
            checked={formData.type === type}
            onChange={handleInputChange}
            style={{ color: '#e5e7eb' }}
          />
        ))}
      </Form.Group>

      <Form.Group>
        <Form.Label>
          Let's Encrypt SSL
          <OverlayTrigger placement="right" overlay={renderTooltip("Automatically provision free SSL certificate from Let's Encrypt")}>
            <FontAwesomeIcon icon={faInfoCircle} className="ms-2 text-muted" style={{ fontSize: '0.8rem', cursor: 'help' }} />
          </OverlayTrigger>
        </Form.Label>
        <Form.Check
          type="checkbox"
          label="Enable Let's Encrypt (Recommended)"
          name="letsEncrypt.enabled"
          checked={formData.letsEncrypt.enabled}
          onChange={(e) => setFormData(prev => ({
            ...prev,
            letsEncrypt: { ...prev.letsEncrypt, enabled: e.target.checked }
          }))}
        />
        {formData.letsEncrypt.enabled && (
          <Form.Control
            type="email"
            placeholder="Email for certificate notifications"
            value={formData.letsEncrypt.email}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              letsEncrypt: { ...prev.letsEncrypt, email: e.target.value }
            }))}
            className="mt-2"
            style={{ backgroundColor: '#374151', color: '#e5e7eb', borderColor: '#4b5563' }}
          />
        )}
      </Form.Group>
    </>
  );

  const renderStep3 = () => (
    <>
      <h6>Filtering & Security</h6>

      <Form.Group className="mb-3">
        <Form.Check
          type="checkbox"
          label={
            <span>
              Enable Geofencing
              <OverlayTrigger placement="right" overlay={renderTooltip("Only allow connections from specific countries (reduces detection risk)")}>
                <FontAwesomeIcon icon={faInfoCircle} className="ms-2 text-muted" style={{ fontSize: '0.8rem', cursor: 'help' }} />
              </OverlayTrigger>
            </span>
          }
          checked={formData.filtering.geoFence.enabled}
          onChange={(e) => setFormData(prev => ({
            ...prev,
            filtering: {
              ...prev.filtering,
              geoFence: { ...prev.filtering.geoFence, enabled: e.target.checked }
            }
          }))}
        />
        {formData.filtering.geoFence.enabled && (
          <Form.Control
            type="text"
            placeholder="Allowed countries (e.g., US,CA,GB)"
            value={formData.filtering.geoFence.allowedCountries.join(',')}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              filtering: {
                ...prev.filtering,
                geoFence: {
                  ...prev.filtering.geoFence,
                  allowedCountries: e.target.value.split(',').map(c => c.trim()).filter(c => c)
                }
              }
            }))}
            className="mt-2"
            style={{ backgroundColor: '#374151', color: '#e5e7eb', borderColor: '#4b5563' }}
          />
        )}
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Check
          type="checkbox"
          label={
            <span>
              Enable Time Window
              <OverlayTrigger placement="right" overlay={renderTooltip("Only forward traffic during business hours (e.g., 9 AM - 5 PM)")}>
                <FontAwesomeIcon icon={faInfoCircle} className="ms-2 text-muted" style={{ fontSize: '0.8rem', cursor: 'help' }} />
              </OverlayTrigger>
            </span>
          }
          checked={formData.filtering.timeWindow.enabled}
          onChange={(e) => setFormData(prev => ({
            ...prev,
            filtering: {
              ...prev.filtering,
              timeWindow: { ...prev.filtering.timeWindow, enabled: e.target.checked }
            }
          }))}
        />
        {formData.filtering.timeWindow.enabled && (
          <div className="mt-2 d-flex gap-2">
            <Form.Control
              type="number"
              min="0"
              max="23"
              placeholder="Start hour"
              value={formData.filtering.timeWindow.startHour}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                filtering: {
                  ...prev.filtering,
                  timeWindow: { ...prev.filtering.timeWindow, startHour: parseInt(e.target.value) }
                }
              }))}
              style={{ backgroundColor: '#374151', color: '#e5e7eb', borderColor: '#4b5563' }}
            />
            <span className="align-self-center" style={{ color: '#e5e7eb' }}>to</span>
            <Form.Control
              type="number"
              min="0"
              max="23"
              placeholder="End hour"
              value={formData.filtering.timeWindow.endHour}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                filtering: {
                  ...prev.filtering,
                  timeWindow: { ...prev.filtering.timeWindow, endHour: parseInt(e.target.value) }
                }
              }))}
              style={{ backgroundColor: '#374151', color: '#e5e7eb', borderColor: '#4b5563' }}
            />
          </div>
        )}
      </Form.Group>
    </>
  );

  const renderStep4 = () => (
    <Form.Group>
      <Form.Label>
        Forward To <span className="text-danger">*</span>
        <OverlayTrigger placement="right" overlay={renderTooltip("Next hop in chain (C2 backend or another redirector)")}>
          <FontAwesomeIcon icon={faInfoCircle} className="ms-2 text-muted" style={{ fontSize: '0.8rem', cursor: 'help' }} />
        </OverlayTrigger>
      </Form.Label>
      <Form.Check
        type="radio"
        label="Forward to C2 Backend"
        name="forwardTo.type"
        value="c2"
        checked={formData.forwardTo.type === 'c2'}
        onChange={() => setFormData(prev => ({
          ...prev,
          forwardTo: { type: 'c2', targetUrl: 'https://185.145.148.203:8443' }
        }))}
      />
      <Form.Check
        type="radio"
        label="Forward to Another Redirector (Chain)"
        name="forwardTo.type"
        value="redirector"
        checked={formData.forwardTo.type === 'redirector'}
        onChange={() => setFormData(prev => ({
          ...prev,
          forwardTo: { type: 'redirector', targetUrl: '' }
        }))}
        className="mb-2"
      />
      <Form.Control
        type="text"
        placeholder="Target URL (e.g., https://c2.internal:8443)"
        value={formData.forwardTo.targetUrl}
        onChange={(e) => setFormData(prev => ({
          ...prev,
          forwardTo: { ...prev.forwardTo, targetUrl: e.target.value }
        }))}
        style={{ backgroundColor: '#374151', color: '#e5e7eb', borderColor: '#4b5563' }}
      />
    </Form.Group>
  );

  const renderStep5 = () => (
    <div>
      <h6>Review Configuration</h6>
      <Table variant="dark" size="sm">
        <tbody>
          <tr><td><strong>Server:</strong></td><td>{servers.find(s => s.id === formData.serverId)?.name}</td></tr>
          <tr><td><strong>Name:</strong></td><td>{formData.name}</td></tr>
          <tr><td><strong>Type:</strong></td><td>{formData.type.toUpperCase()}</td></tr>
          <tr><td><strong>Hostname:</strong></td><td>{formData.hostname}</td></tr>
          <tr><td><strong>IP Address:</strong></td><td>{formData.ipAddress}</td></tr>
          <tr><td><strong>SSL:</strong></td><td>{formData.letsEncrypt.enabled ? `Let's Encrypt (${formData.letsEncrypt.email})` : 'Disabled'}</td></tr>
          <tr><td><strong>Geofencing:</strong></td><td>{formData.filtering.geoFence.enabled ? formData.filtering.geoFence.allowedCountries.join(', ') : 'Disabled'}</td></tr>
          <tr><td><strong>Time Window:</strong></td><td>{formData.filtering.timeWindow.enabled ? `${formData.filtering.timeWindow.startHour}:00 - ${formData.filtering.timeWindow.endHour}:00` : 'Disabled'}</td></tr>
          <tr><td><strong>Forward To:</strong></td><td>{formData.forwardTo.targetUrl}</td></tr>
        </tbody>
      </Table>

      {deploying && deploymentProgress && (
        <div className="mt-3">
          <h6>Deployment Progress</h6>
          <ProgressBar
            now={deploymentProgress.progress}
            label={`${deploymentProgress.progress}%`}
            variant={deploymentProgress.status === 'failed' ? 'danger' : 'info'}
            animated
          />
          <small className="text-muted d-block mt-2">
            {deploymentProgress.currentStep}
          </small>
          {deploymentProgress.error && (
            <Alert variant="danger" className="mt-2">
              {deploymentProgress.error}
            </Alert>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center" style={{ backgroundColor: '#374151', borderColor: '#4b5563', color: '#fff' }}>
          <div>
            <FontAwesomeIcon icon={faServer} className="me-2" />
            Redirector Manager
            <OverlayTrigger placement="right" overlay={renderTooltip("Deploy and manage C2 redirectors for traffic routing")}>
              <FontAwesomeIcon icon={faInfoCircle} className="ms-2" style={{ cursor: 'help', fontSize: '0.9rem' }} />
            </OverlayTrigger>
          </div>
          <Button variant="primary" size="sm" onClick={() => { resetWizard(); setShowDeployWizard(true); }}>
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Deploy Redirector
          </Button>
        </Card.Header>
        <Card.Body style={{ backgroundColor: '#1f2937', color: '#e5e7eb' }}>
          {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
          {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : redirectors.length === 0 ? (
            <Alert variant="info">
              <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
              No redirectors deployed. Click "Deploy Redirector" to get started.
            </Alert>
          ) : (
            <Table hover responsive variant="dark">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Hostname</th>
                  <th>IP Address</th>
                  <th>Status</th>
                  <th>Health</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {redirectors.map(redirector => (
                  <tr key={redirector._id}>
                    <td><strong>{redirector.name}</strong></td>
                    <td><Badge bg="secondary">{redirector.type.toUpperCase()}</Badge></td>
                    <td><code>{redirector.hostname}</code></td>
                    <td><code>{redirector.ipAddress}</code></td>
                    <td>{getStatusBadge(redirector)}</td>
                    <td>
                      {redirector.health.responseTime && (
                        <small className="text-muted">{redirector.health.responseTime}ms</small>
                      )}
                    </td>
                    <td>
                      <Button variant="outline-info" size="sm" className="me-1" onClick={() => handleTest(redirector._id)}>
                        <FontAwesomeIcon icon={faSync} />
                      </Button>
                      <Button variant="outline-warning" size="sm" className="me-1" onClick={() => handleRestart(redirector._id)}>
                        <FontAwesomeIcon icon={faPlay} />
                      </Button>
                      <Button variant="outline-secondary" size="sm" className="me-1" onClick={() => handleViewLogs(redirector)}>
                        <FontAwesomeIcon icon={faFileAlt} />
                      </Button>
                      <Button variant="outline-danger" size="sm" onClick={() => handleDelete(redirector._id)}>
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Deploy Wizard Modal */}
      <Modal show={showDeployWizard} onHide={() => !deploying && setShowDeployWizard(false)} size="lg" centered backdrop={deploying ? 'static' : true}>
        <Modal.Header closeButton={!deploying} style={{ backgroundColor: '#374151', borderColor: '#4b5563', color: '#fff' }}>
          <Modal.Title>
            <FontAwesomeIcon icon={faCog} className="me-2" />
            Deploy Redirector - Step {wizardStep} of 5
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#1f2937', color: '#e5e7eb' }}>
          {wizardStep === 1 && renderStep1()}
          {wizardStep === 2 && renderStep2()}
          {wizardStep === 3 && renderStep3()}
          {wizardStep === 4 && renderStep4()}
          {wizardStep === 5 && renderStep5()}
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#374151', borderColor: '#4b5563' }}>
          {wizardStep > 1 && !deploying && (
            <Button variant="secondary" onClick={prevStep}>
              Back
            </Button>
          )}
          {wizardStep < 5 && (
            <Button variant="primary" onClick={nextStep} disabled={!formData.serverId || !formData.name}>
              Next
            </Button>
          )}
          {wizardStep === 5 && (
            <Button variant="success" onClick={handleDeploy} disabled={deploying}>
              {deploying ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Deploying...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faPlay} className="me-2" />
                  Deploy Now
                </>
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Logs Modal */}
      <Modal show={showLogs} onHide={() => setShowLogs(false)} size="lg" centered>
        <Modal.Header closeButton style={{ backgroundColor: '#374151', borderColor: '#4b5563', color: '#fff' }}>
          <Modal.Title>
            <FontAwesomeIcon icon={faFileAlt} className="me-2" />
            Logs: {selectedRedirector?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#1f2937', color: '#e5e7eb' }}>
          <pre style={{ backgroundColor: '#0d1117', color: '#c9d1d9', padding: '15px', borderRadius: '5px', maxHeight: '500px', overflow: 'auto', fontSize: '0.85rem' }}>
            {logs || 'No logs available'}
          </pre>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#374151', borderColor: '#4b5563' }}>
          <Button variant="secondary" onClick={() => setShowLogs(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default RedirectorManager;
